/**
 * WineCard.js — Fogarty wine listing card (cardType "wine").
 *
 * Pixel/behaviour parity with the old createWineListing card (thomasfogarty/
 * script.js 1850-1910): bottle image, serif red title, winery label,
 * Appellation / Varietal / Alcohol lines, a "Tasting notes" button (clipboard
 * icon) that opens the detailed view, price + "Per bottle", and the cart
 * control — "Add to cart" when the wine is not in the cart, or trash + qty
 * select + "Update" when it is.
 *
 * SCALABLE STRUCTURE:
 *   All cart mutations are events; features/optional/cart.js owns the BE calls.
 *   The card re-renders its action row on ws-cart-count so it flips between
 *   add / update states as the cart changes.
 *
 * EVENTS OUT:
 *   ws-cart-add {variantId, quantity}
 *   ws-cart-update {variantId, quantity}
 *   ws-cart-remove {variantId}
 *   ws-prompt-select {label, value}   (Tasting notes / image → detail view)
 *
 * BE FIELDS (wine_listing): title, label, regular_price, price_unit, sold_out,
 *   available, variant_id, cover_image_link, wine_type, winemaker_notes
 *   { Appellation, Alcohol, Acid, pH }, message (LLM "tell me more" line).
 */

import { emit, on, getState } from "../../../core/state.js";
import { bindWineDrawerOpen } from "../../../utils/openWineDrawer.js";

const MAX_QTY = 50; // old productMaxQty (script.js:188)

const TASTING_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="15" viewBox="0 0 12 15" fill="none"><path d="M3.16667 0.5V3.16667M5.83333 0.5V3.16667M8.5 0.5V3.16667M3.16667 5.83333H7.16667M3.16667 8.5H8.5M3.16667 11.1667H6.5M1.83333 1.83333H9.83333C10.5697 1.83333 11.1667 2.43029 11.1667 3.16667V12.5C11.1667 13.2364 10.5697 13.8333 9.83333 13.8333H1.83333C1.09695 13.8333 0.5 13.2364 0.5 12.5V3.16667C0.5 2.43029 1.09695 1.83333 1.83333 1.83333Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const TRASH_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4.00004H14M12.6667 4.00004V13.3334C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3334V4.00004M5.33333 4.00004V2.66671C5.33333 2.00004 6 1.33337 6.66667 1.33337H9.33333C10 1.33337 10.6667 2.00004 10.6667 2.66671V4.00004" stroke="currentColor" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function isTrue(v) {
  return v === true || v === "true" || v === "True";
}

export default class WineCard {
  render(wine) {
    const variantId = wine.variant_id ?? wine["Variant SKU"] ?? "";
    const soldOut = isTrue(wine.sold_out);
    const unavailable = wine.available !== undefined && !isTrue(wine.available);
    const notes = wine.winemaker_notes || {};
    const detailLabel = wine.message || `Tell me more about ${wine.title || "this wine"}`;

    const card = document.createElement("div");
    card.className = "ws-rich-winecard ws-fg-winecard";
    card.dataset.variantId = variantId;
    if (wine.unique_id != null) card.dataset.uniqueId = String(wine.unique_id);
    if (wine.wine_key) card.dataset.wineKey = String(wine.wine_key);

    // ── head: image (+ sold-out overlay) + text ──
    const head = document.createElement("div");
    head.className = "ws-fg-winecard-head";
    if (wine.cover_image_link) {
      const img = document.createElement("img");
      img.className = "ws-fg-winecard-img";
      img.src = wine.cover_image_link;
      img.alt = wine.title || "Wine";
      img.loading = "lazy";
      // In-stock: image opens detail. Sold-out: the whole card is clickable
      // (old createWineListing), so skip the image-level handler here.
      if (!soldOut) {
        img.addEventListener("click", () => emit("ws-prompt-select", { label: detailLabel, value: detailLabel, uniqueId: wine.unique_id }));
      }
      head.appendChild(img);
    }

    if (soldOut) {
      const overlay = document.createElement("div");
      overlay.className = "ws-fg-winecard-soldout";
      overlay.textContent = "SOLD OUT";
      head.appendChild(overlay);
    }

    card.appendChild(head);

    // ── body: title + details → tasting notes → price + action (old order) ──
    const body = document.createElement("div");
    body.className = "ws-fg-winecard-body";

    if (wine.title) {
      const title = document.createElement("p");
      title.className = "ws-rich-winetitle ws-fg-winecard-title";
      title.title = wine.title;
      title.textContent = wine.title;
      body.appendChild(title);
    }

    // Old .wine_card_desc lines: label + Appellation semibold, Varietal + Alcohol
    // regular. The old sold-out template also listed Acid and PH.
    const light = "ws-fg-winecard-desc ws-fg-winecard-desc-light";
    if (wine.label) body.appendChild(this._line("ws-fg-winecard-desc", wine.label));
    body.appendChild(this._line("ws-fg-winecard-desc", `Appellation ${notes.Appellation || "N/A"}`));
    if (soldOut) body.appendChild(this._line("ws-fg-winecard-desc", `Acid ${notes.Acid || "N/A"}`));
    body.appendChild(this._line(light, `Varietal ${wine.wine_type || "N/A"}`));
    body.appendChild(this._line(light, `Alcohol ${notes.Alcohol || notes.alcohol || "N/A"}`));
    if (soldOut) body.appendChild(this._line(light, `PH ${notes.pH || "N/A"}`));

    // Sold-out cards show price only — no tasting-notes button (old code);
    // the whole card is click-to-detail instead (see card click below).
    if (!soldOut) {
      const tasting = document.createElement("button");
      tasting.type = "button";
      tasting.className = "ws-fg-winecard-tasting";
      tasting.innerHTML = `${TASTING_ICON}<span>Tasting notes</span>`;
      bindWineDrawerOpen(tasting, wine, card);
      body.appendChild(tasting);
    }

    const details = document.createElement("div");
    details.className = "ws-fg-winecard-product-details";

    const footer = document.createElement("div");
    footer.className = "ws-fg-winecard-product-footer";

    const purchase = document.createElement("div");
    purchase.className = "ws-fg-winecard-purchase-row";

    if (wine.regular_price) {
      purchase.appendChild(this._line("ws-fg-price", wine.regular_price));
      // Fogarty wines are sold per bottle. The old card always showed a price
      // unit (wine.price_unit); the new BE omits it for wines, so default to
      // "Per bottle". Fogarty-only — this is the Fogarty WineCard.
      purchase.appendChild(this._line("ws-fg-winecard-unit", wine.price_unit || "Per bottle"));
    }
    details.appendChild(footer);

    // Action row — only for purchasable wines. Flips add ↔ update on cart change.
    if (!soldOut && !unavailable && variantId) {
      const actionMount = document.createElement("div");
      actionMount.className = "ws-fg-winecard-action-mount";
      const renderAction = () => {
        const line = (getState().cart.items || []).find(
          (it) => String(it.productVariantId) === String(variantId)
        );
        actionMount.replaceChildren(
          line ? this._buildUpdate(variantId, Number(line.quantity) || 1) : this._buildAdd(variantId, wine.cta_text)
        );
      };
      renderAction();
      on("ws-cart-count", () => {
        if (actionMount.isConnected) renderAction();
      });
      footer.appendChild(purchase);
      footer.appendChild(actionMount);

      // Spinner overlay over the whole card while a cart action runs — parity
      // with old overlayLoading(wineCard). Shown the moment an action button is
      // clicked; cleared when cart.js signals the mutation finished.
      const overlay = document.createElement("div");
      overlay.className = "ws-cart-overlay";
      overlay.hidden = true;
      card.appendChild(overlay);
      actionMount.addEventListener("click", (e) => {
        if (e.target.closest("button")) overlay.hidden = false;
      });
      on("ws-cart-busy", ({ active } = {}) => {
        if (!active && card.isConnected) overlay.hidden = true;
      });
    }

    body.appendChild(details);
    card.appendChild(body);

    // Sold-out: the entire card opens the detail view (old createWineListing
    // put the onclick on the .card wrapper). In-stock cards keep the image /
    // tasting-button handlers above instead.
    if (soldOut) {
      card.style.cursor = "pointer";
      card.addEventListener("click", () =>
        emit("ws-prompt-select", { label: detailLabel, value: detailLabel, uniqueId: wine.unique_id })
      );
    }

    return card;
  }

  _line(className, text, titleAttr) {
    const p = document.createElement("p");
    p.className = className;
    if (titleAttr) p.title = titleAttr;
    p.textContent = text;
    return p;
  }

  // Not in cart → solid "Add to cart" (adds one). aria-label/title use the
  // wine's cta_text (old createWineListing add-to-cart button).
  _buildAdd(variantId, ctaText) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ws-fg-add";
    btn.textContent = "Add to cart";
    if (ctaText) {
      btn.setAttribute("aria-label", ctaText);
      btn.title = ctaText;
    }
    btn.addEventListener("click", () => emit("ws-cart-add", { variantId, quantity: 1 }));
    return btn;
  }

  // In cart → trash + qty select (current selected) + "Update".
  _buildUpdate(variantId, quantity) {
    const wrap = document.createElement("div");
    wrap.className = "ws-fg-product-action product-action";

    const del = document.createElement("button");
    del.type = "button";
    del.className = "ws-cp-delete delete-cart";
    del.setAttribute("aria-label", "delete from basket");
    del.title = "delete from basket";
    del.dataset.variantId = variantId;
    del.innerHTML = TRASH_ICON;
    del.addEventListener("click", () => emit("ws-cart-remove", { variantId }));

    const qtyWrap = document.createElement("div");
    qtyWrap.className = "ws-cp-qty-wrap product-quantity-wrapper";

    const dec = document.createElement("button");
    dec.type = "button";
    dec.className = "ws-cp-dec dec-qty";
    dec.setAttribute("aria-label", "Decrease quantity");
    dec.innerHTML = '<span aria-hidden="true">−</span>';
    // Old .dec-qty parity: at qty 1, minus removes the line (handleDeleteFromCart);
    // otherwise it steps the quantity down by one.
    dec.addEventListener("click", () => {
      if (quantity <= 1) {
        emit("ws-cart-remove", { variantId });
        return;
      }
      emit("ws-cart-update", { variantId, quantity: quantity - 1 });
    });

    const qty = document.createElement("div");
    qty.className = "ws-cp-cart-qty cart-qty";
    qty.textContent = String(quantity);

    const inc = document.createElement("button");
    inc.type = "button";
    inc.className = "ws-cp-inc inc-qty";
    inc.setAttribute("aria-label", "Increase quantity");
    inc.innerHTML = '<span aria-hidden="true">+</span>';
    inc.addEventListener("click", () =>
      emit("ws-cart-update", {
        variantId,
        quantity: Math.min(12, quantity + 1),
      }),
    );

    qtyWrap.append(dec, qty, inc);
    wrap.append(del, qtyWrap);
    return wrap;
  }
}
